import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Tracks an element's live content-box width. Returns `null` until the first
 * measurement lands (ref not yet attached) — callers should treat `null` as
 * "don't know yet, don't apply width-dependent logic".
 *
 * Takes an immediate synchronous `getBoundingClientRect` reading on mount
 * (not just a ResizeObserver subscription) — some environments create the
 * observer fine but never deliver its first async callback, which would
 * otherwise leave callers stuck on `null` forever.
 */
export function useContainerWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number | null] {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.getBoundingClientRect().width);

    // jsdom (unit tests) and very old browsers don't implement
    // ResizeObserver — the initial measurement above still applies, later
    // resizes just won't be tracked there.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}
